/* eslint-disable @typescript-eslint/prefer-optional-chain */
// istanbul ignore file
import React, { useEffect } from 'react'
import { CircularProgress } from '@mui/material'
import AuthError from '../../../components/ErrorComponents/AuthError'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import { type RootState } from '../../../store'
import { type BubbleKpiState } from '../types'
import { buildDonutChartHistoryData, type GeneratedDonutData } from './KpiComponents/BuildKpiHistory'
import { type KpiKey, type KpiKeyBubble } from '../redux/changeKpiType'
import { getKpiIdFromName, getKpiService } from './KpiComponents/kpiUtils'
import { type DonutSeriesData } from './KpiComponents/DonutChart'
import { kpiColorMapDonut } from './KpiComponents/BuildKpiData'
import { fetchSpreadFactor } from '../redux/actionCreators'

export interface KpiSeriesData {
  name: string
  data: number[]
}

export type KpiDispatchLoader = (kpiType: string, service: string, kpiCategoryId: string) => void

export const getKpiType = (kpiType: KpiKey): string => {
  switch (kpiType) {
    case 'Spread Factor':
      return 'spread-factor-breakdown'
    default:
      return 'key-not-present'
  }
}

export interface KpiHistoryState <T> {
  isLoading: boolean
  error: boolean
  data: T[] | undefined
  httpStatus: number
}

interface ParentProps {
  selectedBubble: number
  setSelectedBubble: React.Dispatch<React.SetStateAction<number>>
  setBarColor: React.Dispatch<React.SetStateAction<string>>
}

function HistoryBubbleChart (props: AllProps): JSX.Element {
  const initialReportData: DonutSeriesData = {
    name: [],
    data: [],
    color: []
  }
  const [reportData, setReportData] = React.useState<DonutSeriesData>(initialReportData)
  const selectedKpi = useSelector((state: RootState) => state.kpiType)

  function kpiData (): GeneratedDonutData {
    return buildDonutChartHistoryData(selectedKpi as KpiKeyBubble, props.spreadFactor)
  }

  const kpiLoader = (): KpiDispatchLoader => {
    if (selectedKpi === 'Spread Factor') {
      return props.fetchSpreadFactor
    } else {
      return () => undefined
    }
  }

  const state = (): BubbleKpiState => {
    return props.spreadFactor
  }

  const loadKpiData = (): void => {
    const loader = kpiLoader()
    loader(getKpiType(selectedKpi), getKpiService(selectedKpi), getKpiIdFromName(selectedKpi) ?? '')
  }

  useEffect(() => {
    loadKpiData()
  }, [])

  useEffect(() => {
    if (state().httpStatus === 200 && state().data !== undefined) {
      setReportData(kpiData())
    }
  }, [state().data])

  function getShortenedName (name: string, kpiName: KpiKey): JSX.Element {
    if (kpiName === 'Spread Factor') {
      // Extract trailing number
      const m = name.match(/(\d+)\s*$/)
      if (m !== null && m !== undefined) {
        const x = parseInt(m[1], 10)
        return <span>{x}</span>
      }
      return <span></span>
    }
    // for other bubble kpis
    return <span>{name}</span>
  }

  const handleGraphWithFailure = (): JSX.Element => {
    const kpiState = state()
    if (kpiState.isLoading) {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></div>
      )
    } else if (kpiState.httpStatus === 401) {
      return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="Unauthorized"/></div>
      )
    } else if (kpiState.httpStatus === 403) {
      return (<div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="accessForbidden"/></div>)
    } else if (kpiState.httpStatus === 200 && kpiState.data === undefined) {
      return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="NoDataPresent"/></div>
      )
    } else if (kpiState.data !== undefined && kpiState.httpStatus === 200) {
      return (
        <>
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 35 }}>
            {reportData.name.map((data, index) => (
              <div
                key={index}
                style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', borderBottom: (index === props.selectedBubble) ? '2px solid #64C3FF' : 'none', paddingBottom: (index === props.selectedBubble) ? '2px' : 0 }}
                onClick={() => {
                  props.setSelectedBubble(index)
                  props.setBarColor(kpiColorMapDonut[selectedKpi as KpiKeyBubble][index])
                }}
              >
                <span style={{ backgroundColor: kpiColorMapDonut[selectedKpi as KpiKeyBubble][index], width: '10px', height: '10px' }}></span>
                <span style={{ fontSize: 12, fontFamily: 'Honeywell Sans Web', fontWeight: (index === props.selectedBubble) ? 700 : 500, color: (index === props.selectedBubble) ? '#F0F0F0' : '#B0B0B0' }}>{getShortenedName(data, selectedKpi)}</span>
              </div>
            ))}
          </div>
        </>
      )
    } else {
      return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="cannotFetch" retry={loadKpiData}/></div>
      )
    }
  }

  return (
    <div className='kpiHistoryContainer' style={{ display: 'flex', alignItems: 'center', borderRadius: 0, padding: 10, border: 'none' }}>
      {handleGraphWithFailure()}
    </div>
  )
}

interface DispatchToProps {
  fetchSpreadFactor: (kpiType: string, service: string, kpiCategoryId: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchSpreadFactor: (kpiType: string, service: string, kpiCategoryId: string) => dispatch(fetchSpreadFactor(kpiType, service, kpiCategoryId))
})
interface StateToProps {
  spreadFactor: BubbleKpiState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  spreadFactor: state.SpreadFactorState
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
type AllProps = ParentProps & PropsFromRedux
export default connector(HistoryBubbleChart)
